import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../ui/carousel';
import { X, ImageIcon } from 'lucide-react';

const ResortGallery = ({ resort, trigger }) => {
  const [open, setOpen] = useState(false);
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const images = resort.gallery && resort.gallery.length ? resort.gallery : [resort.image];

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on('select', onSelect);
    return () => api.off('select', onSelect);
  }, [api]);

  // Keyboard navigation
  useEffect(() => {
    if (!open || !api) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') api.scrollPrev();
      if (e.key === 'ArrowRight') api.scrollNext();
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, api]);

  const scrollTo = useCallback((i) => api && api.scrollTo(i), [api]);

  return (
    <>
      <span onClick={() => setOpen(true)} className="inline-block cursor-pointer">
        {trigger}
      </span>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="karma-gallery p-0 border-0 max-w-none w-screen h-[100dvh] sm:h-[95vh] sm:w-[95vw] sm:max-w-6xl sm:rounded-sm overflow-hidden [&>button.absolute]:hidden"
          style={{ background: '#0F2340' }}
        >
          <DialogTitle className="sr-only">{resort.name} gallery</DialogTitle>

          {/* Close button — safe-area aware, always visible on mobile */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close gallery"
            data-testid="gallery-close-button"
            className="z-40 inline-flex items-center gap-2 rounded-full border border-white/40 backdrop-blur-sm px-3 py-2 sm:w-10 sm:h-10 sm:p-0 sm:justify-center transition-colors"
            style={{
              position: 'absolute',
              color: '#FBF7EE',
              background: 'rgba(15, 35, 64, 0.65)',
              top: 'max(1rem, env(safe-area-inset-top))',
              right: 'max(1rem, env(safe-area-inset-right))',
            }}
          >
            <X size={18} />
            <span className="chapter-badge uppercase sm:hidden">Close</span>
          </button>

          {/* Mobile-only bottom close fallback (thumb reach) */}
          <button
            onClick={() => setOpen(false)}
            aria-label="Close gallery"
            data-testid="gallery-close-button-mobile"
            className="sm:hidden z-40 chapter-badge uppercase rounded-full border border-white/40 backdrop-blur-sm px-5 py-2.5"
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              color: '#FBF7EE',
              background: 'rgba(15, 35, 64, 0.65)',
              bottom: 'max(1rem, env(safe-area-inset-bottom))',
            }}
          >
            Close Gallery
          </button>

          {/* Resort meta */}
          <div className="absolute top-4 left-4 md:top-6 md:left-6 z-30 max-w-[70%]">
            <div className="chapter-badge uppercase" style={{ color: '#C9A75E' }}>
              {resort.place}
            </div>
            <div className="font-serif-display text-2xl md:text-3xl mt-1" style={{ color: '#FBF7EE' }}>
              {resort.name}
            </div>
          </div>

          {/* Carousel */}
          <div className="w-full h-full flex items-center justify-center">
            <Carousel
              setApi={setApi}
              opts={{ loop: true, align: 'center' }}
              className="w-full h-full"
            >
              <CarouselContent className="h-[100dvh] sm:h-[95vh] -ml-0">
                {images.map((src, i) => (
                  <CarouselItem key={i} className="pl-0 basis-full h-full flex items-center justify-center">
                    <div className="w-full h-full flex items-center justify-center px-2 sm:px-16">
                      <img
                        src={src}
                        alt={`${resort.name} — ${i + 1}`}
                        className="max-w-full max-h-[80dvh] sm:max-h-[80vh] w-auto h-auto object-contain shadow-2xl"
                        loading={i === 0 ? 'eager' : 'lazy'}
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>

              {/* Desktop arrows */}
              <CarouselPrevious
                className="hidden sm:flex left-4 md:left-6 bg-transparent border-white/40 hover:bg-white/10"
                style={{ color: '#FBF7EE' }}
              />
              <CarouselNext
                className="hidden sm:flex right-4 md:right-6 bg-transparent border-white/40 hover:bg-white/10"
                style={{ color: '#FBF7EE' }}
              />
            </Carousel>
          </div>

          {/* Footer indicator + thumbnails */}
          <div className="absolute bottom-16 sm:bottom-0 left-0 right-0 z-30 pb-2 sm:pb-6 px-4">
            <div className="flex flex-col items-center gap-3">
              <div className="chapter-badge uppercase" style={{ color: 'rgba(251,247,238,0.75)' }}>
                {String(current + 1).padStart(2, '0')} / {String(images.length).padStart(2, '0')}
              </div>
              <div className="flex items-center gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => scrollTo(i)}
                    aria-label={`Go to image ${i + 1}`}
                    className="transition-all duration-300"
                    style={{
                      width: i === current ? 28 : 8,
                      height: 4,
                      background: i === current ? '#C9A75E' : 'rgba(251,247,238,0.35)',
                    }}
                  />
                ))}
              </div>
              <div className="hidden sm:block chapter-badge uppercase" style={{ color: 'rgba(251,247,238,0.5)' }}>
                Swipe or use arrow keys · Esc to close
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const GalleryTriggerButton = ({ count }) => (
  <span className="inline-flex items-center gap-2 chapter-badge uppercase text-navy border border-divider bg-warm px-3 py-2 hover:bg-navy hover:text-warm transition-colors duration-300">
    <ImageIcon size={12} />
    View Gallery {count ? `· ${count}` : ''}
  </span>
);

export const GalleryTriggerOverlay = () => (
  <div className="absolute inset-0 bg-navy/0 hover:bg-navy/30 transition-colors duration-500 flex items-center justify-center opacity-0 hover:opacity-100">
    <span className="inline-flex items-center gap-2 chapter-badge uppercase text-warm bg-navy/70 border border-white/30 px-4 py-2 backdrop-blur">
      <ImageIcon size={13} />
      View Gallery
    </span>
  </div>
);

export default ResortGallery;
